import { WizardFactory } from "@/shared/Wizard/services/WizardFactory";
import { FileDigit, FileJson } from "lucide-vue-next";
import { h } from "vue";
import { WizardStepContent } from "@/shared/Wizard/models/WizardStepContent";
import { WizardStepStatus } from "@/shared/Wizard/models/WizardStepStatus";
import { AplosFile } from "@/components/FileManager/models/AplosFile";
import { Execution } from "@/shared/Executions/models/Execution";
import { AdvancedAnalysisWizardSharedModel } from "../models/AdvancedAnaylsisWizardSharedModel";
import { AdvancedAnalysisWizardStep } from "../models/AdvancedAnalysisWizardStep";
import { AdvancedAnalysisWizardModel } from "../models/AdvancedAnalysisWizardModel";
import { AdvancedAnalysisWizardStepType } from "../models/AdvancedAnyalsisWizardStepType";
import AdvancedAnalysisInputDataStep from "../InputDataStep/AdvancedAnalysisInputDataStep.vue";
import AdvancedAnalysisInputDataStepStatus from "../InputDataStep/components/AdvancedAnalysisInputDataStepStatus.vue";
import AdvancedAnalysisConfig from "@/components/AdvancedAnalysis/AdvancedAnalysisConfig.vue";
import { AdvancedAnalysis } from "@/components/AdvancedAnalysis/models/AdvancedAnalysis";
import AdvancedAnalysisStepStatus from "../AdvancedAnalysis/components/AdvancedAnalysisStepStatus.vue";


export class AdvancedAnalysisWizardFactory extends WizardFactory<
    AdvancedAnalysisWizardSharedModel,
    AdvancedAnalysisWizardStep,
    AdvancedAnalysisWizardModel> {

    static readonly injectableKey = 'AdvancedAnalysisWizardFactory';

    get injectableKey() {
        return (this.constructor as typeof AdvancedAnalysisWizardFactory).injectableKey;
    }

    public createWizardModel(): AdvancedAnalysisWizardModel {

        const steps: Array<AdvancedAnalysisWizardStep> = [

            (() => {
                const s = new AdvancedAnalysisWizardStep(
                    'Input Data',
                    h(FileDigit),
                    new WizardStepContent<AdvancedAnalysisWizardSharedModel>(
                        "Input Data",
                        AdvancedAnalysisInputDataStep,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                wizardModel,
                                ['onUpdate:inputFile']: (val: AplosFile | undefined) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.inputFile = val;
                                    this.resetDependentSharedModelValues(wizardModel.sharedModel, AdvancedAnalysisWizardStepType.InputData);
                                },
                            };
                        }
                    ),
                    AdvancedAnalysisWizardStepType.InputData,
                    AdvancedAnalysisInputDataStepStatus
                );
                s.status = WizardStepStatus.InProgress;
                return s;
            })(),

            (() => {
                const s = new AdvancedAnalysisWizardStep(
                    'Advanced Analysis',
                    h(FileJson),
                    new WizardStepContent<AdvancedAnalysisWizardSharedModel>(
                        "Advanced Analysis",
                        AdvancedAnalysisConfig,
                        (step, stepContent, wizardModel) => {
                            return {
                                step,
                                stepContent,
                                wizardModel,
                                fileSelectionDisabled: true,
                                mode: 'singleconfig',
                                analysisFile: wizardModel?.sharedModel?.inputFile,
                                advancedAnalysis: wizardModel?.sharedModel?.advancedAnalysis,
                                advancedAnalysisExecution: wizardModel?.sharedModel?.advancedAnalysisExecution,
                                ['onUpdate:advancedAnalysis']: (val?: AdvancedAnalysis) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.advancedAnalysis = val;
                                },
                                ['onUpdate:advancedAnalysisExecution']: (val?: Execution) => {
                                    if (!wizardModel?.sharedModel) return;
                                    wizardModel.sharedModel.advancedAnalysisExecution = val;
                                }
                            };
                        }
                    ),
                    AdvancedAnalysisWizardStepType.AdvancedAnalysisConfig,
                    AdvancedAnalysisStepStatus
                );
                s.status = WizardStepStatus.Waiting;
                s.disabled = false;
                return s;
            })(),

        ];

        const wizard = new AdvancedAnalysisWizardModel(steps);
        wizard.activeStep = steps[0];

        return wizard;
    }

    private resetDependentSharedModelValues(sharedModel?: AdvancedAnalysisWizardSharedModel, from?: AdvancedAnalysisWizardStepType): void {
        if (!sharedModel || from === undefined) return;

        const ordered: AdvancedAnalysisWizardStepType[] = [
            AdvancedAnalysisWizardStepType.InputData,
            AdvancedAnalysisWizardStepType.AdvancedAnalysisConfig
        ];

        const resetMap: Record<string, (m: AdvancedAnalysisWizardSharedModel) => void> = {
            [AdvancedAnalysisWizardStepType.InputData]: (m: AdvancedAnalysisWizardSharedModel) => {
                m.advancedAnalysis = undefined;
                m.advancedAnalysisExecution = undefined;
            }
        };

        const start = ordered.indexOf(from);
        if (start < 0) return;
        for (let i = start; i < ordered.length; i++) {
            const key = ordered[i];
            const fn = resetMap[key];
            if (fn) fn(sharedModel);
        }
    }

}